import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { trackEventWithUTM } from '../../utils/analytics';
import OptimizedImage from '../OptimizedImage';
import './PopularPujas.css';

// ─── Data ──────────────────────────────────────────────────────────────────────
interface PujaItem {
  id: number;
  name: string;
  category: string;
  image: string;
  price: string;
  priests: string;
  bestFor: string;
  bookingPath: string;
  detailPath: string;
  badge?: string | null;
  availableAt: 'home-only' | 'home-and-kashi' | 'kashi';
}

const POPULAR_PUJAS: PujaItem[] = [
  {
    id: 2,
    name: 'Rudrabhishekh',
    category: 'Shiv Pujan',
    image: '/images/rudrabhishek.jpg',
    price: '₹25,000',
    priests: '1, 5 or 11',
    bestFor: 'Spiritual cleansing & peace of mind',
    bookingPath: '/booking/shiv-pujan/rudrabhishekh',
    detailPath: '/service/shiv-pujan',
    availableAt: 'home-and-kashi',
  },
  {
    id: 1,
    name: 'Maha Mrityunjaya Pujan',
    category: 'Shiv Pujan',
    image: '/images/maha_mritunjay.jpg',
    price: '₹21,500',
    priests: '1, 3 or 5',
    bestFor: 'Health, longevity & protection',
    bookingPath: '/booking/shiv-pujan/maha-mrityunjaya-pujan',
    detailPath: '/service/shiv-pujan',
    badge: 'Powerful',
    availableAt: 'home-and-kashi',
  },
  {
    id: 18,
    name: 'Shubh Vivah',
    category: 'Sanskar',
    image: '/images/sanskar/vivah.jpg',
    price: '₹35,000',
    priests: '2, 3 or 5',
    bestFor: 'Divine protection & strength',
    bookingPath: '/booking/vishist-sanskar/shubh-vivah',
    detailPath: '/service/sanskar/shubh-vivah',
    badge: null,
    availableAt: 'home-only',
  },
  {
    id: 7,
    name: 'Shani Shanti Pujan',
    category: 'Navagrah Pujan',
    image: '/images/navgrah/shani.jpg',
    price: '₹21,000',
    priests: '2, 4 or 7',
    bestFor: 'Career obstacles & financial problems',
    bookingPath: '/booking/navagrah-pujan/shani-shanti-pujan',
    detailPath: '/service/navagrah-pujan',
    availableAt: 'kashi',
  },
  {
    id: 20,
    name: 'Satyanarayan Pujan',
    category: 'Shanti & Samridhi',
    image: '/images/shanti_samridhi/satyanarayan.jpg',
    price: '₹21,000',
    priests: '1, 2 or 3',
    bestFor: 'Wish fulfillment & family harmony',
    bookingPath: '/booking/shanti-samridhi/satyanarayan-pujan',
    detailPath: '/service/shanti-samridhi',
    availableAt: 'home-only',
  },
  {
    id: 19,
    name: 'Grih Pravesh Pujan',
    category: 'Shanti & Samridhi',
    image: '/images/shanti_samridhi/grih_pravesh.jpg',
    price: '₹35,000',
    priests: '2, 3 or 4',
    bestFor: 'New home entry & Vastu correction',
    bookingPath: '/booking/shanti-samridhi/grih-pravesh-pujan',
    detailPath: '/service/shanti-samridhi',
    badge: null,
    availableAt: 'home-only',
  },
];

// ─── Component ─────────────────────────────────────────────────────────────────
const PopularPujas: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackEventWithUTM('homepage_section_viewed', 'homepage', 'popular_pujas');
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="popular-pujas" id="popular-pujas">
      {/* ── Section header ──────────────────────────────────────── */}
      <p className="popular-pujas__eyebrow">Explore Puja Services</p>
      <h2 className="popular-pujas__title">Pooja Services</h2>
      <p className="popular-pujas__subtitle">
        Kashi-certified Acharyas. Authentic Vedic rituals. Performed at your home - with full guidance.
      </p>
      {/* <p className="popular-pujas__location-note">
        📍 Prices shown for at-home service in Delhi NCR
      </p> */}

      {/* ── Grid ────────────────────────────────────────────────── */}
      <div className="popular-pujas__grid">
        {POPULAR_PUJAS.map((puja) => (
          <article className="puja-card" key={puja.id}>
            {/* Image */}
            <div className="puja-card__image-wrap">
              <OptimizedImage
                src={puja.image}
                alt={puja.name}
                className="puja-card__image"
              />
              <span className="puja-card__category">{puja.category}</span>
              {puja.badge && (
                <span className="puja-card__badge">{puja.badge}</span>
              )}
            </div>

            {/* Body */}
            <div className="puja-card__body">
              <h3 className="puja-card__name">{puja.name}</h3>
              <span className="puja-card__location-tag">
                {puja.availableAt === 'home-and-kashi'
                  ? '🏠 Delhi NCR · 🛕 Also at Kashi'
                  : puja.availableAt === 'kashi'
                  ? '🛕 At Kashi'
                  : '🏠 Delhi NCR (At Home)'}
              </span>
              <p className="puja-card__best-for">
                <strong>Best for:</strong> {puja.bestFor}
              </p>

              {/* Price + priests */}
              <div className="puja-card__meta">
                <div>
                  <span className="puja-card__price">{puja.price}</span>
                  <span className="puja-card__price-label">Starting price</span>
                  <span className="puja-card__price-label">All Rituals & Samagri</span>
                </div>
                <span className="puja-card__priests">
                  <span className="puja-card__priests-icon">Choose: </span>
                  {puja.priests} Acharya{parseInt(puja.priests) > 1 ? 's' : '(s)'}
                </span>
              </div>

              {/* CTAs */}
              <div className="puja-card__actions">
                <Link
                  to={puja.bookingPath}
                  className="puja-card__book-btn"
                  onClick={() =>
                    trackEventWithUTM('homepage_popular_puja_click', 'popular_pujas', puja.name, {
                      action: 'book_now',
                      puja_id: puja.id,
                      price: puja.price,
                    })
                  }
                >
                  Book Now →
                </Link>
                {/* <Link
                  to={puja.detailPath}
                  className="puja-card__details-link"
                  onClick={() =>
                    trackEventWithUTM('homepage_popular_puja_click', 'popular_pujas', puja.name, {
                      action: 'view_details',
                      puja_id: puja.id,
                    })
                  }
                >
                  View Details
                </Link> */}
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* ── Footer CTA ──────────────────────────────────────────── */}
      <div className="popular-pujas__footer">
        <button
          className="popular-pujas__all-link"
          onClick={() => {
            trackEventWithUTM('homepage_browse_all_click', 'popular_pujas', 'browse_all_pujas');
            const el = document.getElementById('services');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        >
          Browse All Pujas &amp; Ceremonies →
        </button>
      </div>
    </section>
  );
};

export default PopularPujas;
